import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { fmtDA, fmtDate, todayISO } from "@/lib/format";
import { Landmark, Plus, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function Banque() {
  const { societes, societeParam, societeId } = useApp();
  const [comptes, setComptes] = useState([]);
  const [mouvements, setMouvements] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [mvOpen, setMvOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [mv, setMv] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/banque/comptes", { params: societeParam });
      setComptes(data);
      const { data: m } = await api.get("/banque/mouvements", { params: societeParam });
      setMouvements(m.items);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [societeId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const sid = () => (societeId !== "all" ? societeId : societes[0]?.id || "");

  const openNew = () => { setForm({ societe_id: sid(), banque: "", nom_compte: "", numero_compte: "", devise: "DA", solde_initial: "" }); setOpen(true); };
  const openMv = () => { setMv({ societe_id: sid(), compte_id: comptes[0]?.id || "", date: todayISO(), type: "encaissement", sens: "entree", montant: "", description: "" }); setMvOpen(true); };

  const submit = async () => {
    if (!form.societe_id || !form.banque || !form.nom_compte) return toast.error("Champs requis manquants.");
    try {
      await api.post("/banque/comptes", { ...form, solde_initial: Number(form.solde_initial) || 0 });
      toast.success("Compte créé.");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitMv = async () => {
    if (!mv.compte_id) return toast.error("Compte requis.");
    if (!mv.montant || Number(mv.montant) <= 0) return toast.error("Montant invalide.");
    const sens = mv.type === "encaissement" ? "entree" : "sortie";
    try {
      await api.post("/banque/mouvements", { ...mv, sens, montant: Number(mv.montant) });
      toast.success("Mouvement enregistré.");
      setMvOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const shown = selected ? mouvements.filter((m) => m.compte_id === selected) : mouvements;
  const compteName = (id) => { const c = comptes.find((x) => x.id === id); return c ? `${c.banque} · ${c.nom_compte}` : "—"; };

  return (
    <div>
      <PageHeader
        title="Banque"
        subtitle="Comptes bancaires et mouvements"
        testid="banque-page"
        actions={
          <div className="flex gap-2">
            {comptes.length > 0 && <Button variant="outline" onClick={openMv} className="gap-2" data-testid="new-bank-mv-btn"><Plus className="h-4 w-4" /> Mouvement</Button>}
            <Button onClick={openNew} className="gap-2" data-testid="new-compte-btn"><Plus className="h-4 w-4" /> Compte</Button>
          </div>
        }
      />

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <>
          {comptes.length === 0 ? (
            <div className="rounded-xl border bg-card">
              <EmptyState icon={Landmark} title="Aucun compte bancaire" hint="Créez un compte pour suivre encaissements, décaissements et virements." />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 kpi-grid">
              <button onClick={() => setSelected(null)} className={`text-left rounded-xl border p-5 transition-colors ${!selected ? "border-primary bg-accent/50" : "bg-card hover:border-primary/40"}`} data-testid="compte-all">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Tous les comptes</p>
                <p className="tabular text-2xl font-bold text-primary mt-2">{fmtDA(comptes.reduce((s, c) => s + c.solde, 0))}</p>
                <p className="text-xs text-muted-foreground mt-1">{comptes.length} compte(s)</p>
              </button>
              {comptes.map((c) => (
                <button key={c.id} onClick={() => setSelected(c.id)} className={`text-left rounded-xl border p-5 transition-colors ${selected === c.id ? "border-primary bg-accent/50" : "bg-card hover:border-primary/40"}`} data-testid={`compte-${c.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{c.banque}</p>
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="tabular text-2xl font-bold mt-2 text-success">{fmtDA(c.solde)}</p>
                  <p className="text-xs text-muted-foreground mt-1">{c.nom_compte} · {c.numero_compte || "—"}</p>
                </button>
              ))}
            </div>
          )}

          {comptes.length > 0 && (
            <div className="rounded-xl border bg-card overflow-hidden">
              <div className="px-5 py-4 border-b"><h3 className="font-heading font-bold">Mouvements {selected && `· ${compteName(selected)}`}</h3></div>
              {shown.length === 0 ? (
                <p className="text-sm text-muted-foreground py-10 text-center">Aucun mouvement.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="banque-table">
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Date</TableHead>
                        <TableHead>Compte</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Montant</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {shown.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell className="tabular text-sm whitespace-nowrap">{fmtDate(m.date)}</TableCell>
                          <TableCell className="text-sm">{compteName(m.compte_id)}</TableCell>
                          <TableCell className="text-sm capitalize">{m.type.replace(/_/g, " ")}</TableCell>
                          <TableCell className="text-sm max-w-[280px] truncate">{m.description || "—"}</TableCell>
                          <TableCell className={`tabular text-right font-semibold whitespace-nowrap ${m.sens === "entree" ? "text-success" : "text-destructive"}`}>
                            {m.sens === "entree" ? "+" : "−"} {fmtDA(m.montant)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-md" data-testid="compte-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Nouveau compte bancaire</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <Label>Société</Label>
                <Select value={form.societe_id} onValueChange={(v) => setForm((f) => ({ ...f, societe_id: v }))}>
                  <SelectTrigger data-testid="compte-societe"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent className="bg-popover">{societes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Banque *</Label><Input data-testid="compte-banque" value={form.banque} onChange={(e) => setForm((f) => ({ ...f, banque: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Nom du compte *</Label><Input data-testid="compte-nom" value={form.nom_compte} onChange={(e) => setForm((f) => ({ ...f, nom_compte: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>N° de compte</Label><Input value={form.numero_compte} onChange={(e) => setForm((f) => ({ ...f, numero_compte: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Solde initial</Label><Input type="number" step="0.01" value={form.solde_initial} onChange={(e) => setForm((f) => ({ ...f, solde_initial: e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} data-testid="compte-submit">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mvOpen} onOpenChange={setMvOpen}>
        <DialogContent className="bg-popover max-w-md" data-testid="bank-mv-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Mouvement bancaire</DialogTitle></DialogHeader>
          {mv && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <Label>Compte</Label>
                <Select value={mv.compte_id} onValueChange={(v) => setMv((f) => ({ ...f, compte_id: v }))}>
                  <SelectTrigger data-testid="bmv-compte"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent className="bg-popover">{comptes.map((c) => <SelectItem key={c.id} value={c.id}>{c.banque} · {c.nom_compte}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={mv.type} onValueChange={(v) => setMv((f) => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="bmv-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="encaissement">Encaissement (+)</SelectItem>
                    <SelectItem value="decaissement">Décaissement (−)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Montant *</Label><Input data-testid="bmv-montant" type="number" step="0.01" value={mv.montant} onChange={(e) => setMv((f) => ({ ...f, montant: e.target.value }))} /></div>
              <div className="space-y-1.5 col-span-2"><Label>Date</Label><DatePicker value={mv.date} onChange={(v) => setMv((f) => ({ ...f, date: v }))} testid="bmv-date" /></div>
              <div className="space-y-1.5 col-span-2"><Label>Description</Label><Input value={mv.description} onChange={(e) => setMv((f) => ({ ...f, description: e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMvOpen(false)}>Annuler</Button>
            <Button onClick={submitMv} data-testid="bmv-submit">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
